import React from "react";

export function StatusPill({ children, tone = "default" }) {
  const toneClass = {
    default: "border-primary/12 bg-primary/8 text-primary",
    warning: "border-[#D8B774]/35 bg-[#FFF8E7] text-[#8A6B22]",
    positive: "border-[#5FA36A]/30 bg-[#EFF8EF] text-[#2F6B3A]",
    danger: "border-[#D68B72]/35 bg-[#FFF1EC] text-[#8A3D22]",
    neutral: "border-[#C7D3BF] bg-[#EEF4E7] text-textMain",
  };

  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${toneClass[tone]}`}>
      {children}
    </span>
  );
}

export function SummaryTile({ icon: Icon, label, value, description }) {
  return (
    <div className="platform-elevated-surface rounded-[28px] border p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">{label}</p>
          <p className="mt-3 font-heading text-4xl font-semibold text-primary">{value}</p>
          <p className="mt-2 text-sm leading-6 text-mutedForeground">{description}</p>
        </div>
        {Icon ? (
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function SectionPanel({ title, eyebrow, description, actions, children, className = "", ...props }) {
  return (
    <section className={`platform-elevated-surface rounded-[30px] border p-6 ${className}`} {...props}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          {eyebrow ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">{eyebrow}</p>
          ) : null}
          <h2 className="mt-2 font-heading text-2xl font-semibold text-primary">{title}</h2>
          {description ? <p id={props.id ? `${props.id}-description` : undefined} className="mt-2 max-w-3xl text-sm leading-6 text-mutedForeground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="platform-soft-panel rounded-[28px] border border-dashed border-primary/15 px-6 py-12 text-center">
      {Icon ? <Icon className="mx-auto h-12 w-12 text-primary/55" /> : null}
      <h3 className="mt-4 font-heading text-2xl font-semibold text-primary">{title}</h3>
      <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-mutedForeground">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function PanelActionButton({ children, tone = "primary", ...props }) {
  const toneClass = {
    primary: "bg-primary text-primaryForeground hover:bg-[#274C31]",
    secondary: "border border-primary/15 bg-[#ECF4E6] text-primary hover:bg-[#E1ECD8]",
    danger: "bg-[#8A3D22] text-white hover:bg-[#72331D]",
  };

  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${toneClass[tone]}`}
      {...props}
    >
      {children}
    </button>
  );
}
